import React, { useState } from 'react';
import { FiDownload, FiBarChart2, FiFileText, FiPieChart, FiCalendar, FiFilter, FiExternalLink, FiClipboard,
  FiActivity, FiDollarSign, FiMapPin, FiAward, FiTrendingUp, FiInfo } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { Heading } from '../components/ui/heading';
import ClosureReport from '../components/ClosureReport';
import { useFramework } from '../contexts/FrameworkContext';
import { useCustomer } from '../contexts/CustomerContext';

const ReportExport: React.FC = () => {
  const { framework } = useFramework();
  const { customer } = useCustomer();
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [viewingClosureReport, setViewingClosureReport] = useState(false);
  const [closureReportId, setClosureReportId] = useState<string>('');
  
  const handleReportSelect = (reportId: string) => {
    setSelectedReport(reportId);
  };
  
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'start' | 'end') => {
    setDateRange(prev => ({
      ...prev,
      [type]: e.target.value
    }));
  };
  
  const handleGenerateReport = () => {
    if (selectedReport === 'product-closure') {
      // Show the closure report
      setClosureReportId('prod-001');
      setViewingClosureReport(true);
    } else {
      // In a real app, this would trigger other report generation
      console.log('Generating report:', selectedReport, 'with date range:', dateRange);
      // For demo purposes, we could show a success message or simulate a download
    }
  };
  
  const handleCloseClosureReport = () => {
    setViewingClosureReport(false);
  };
  
  // Define all report types
  const allReportTypes = [
    {
      id: 'ncd-compliance',
      title: 'NCD Compliance Overview',
      description: 'Overview of NCD compliance across all categories with trends and insights',
      icon: FiBarChart2,
      format: 'PDF, Excel',
      tymeBank: true,
      framework: 'NCD',
      clientName: ''
    },
    {
      id: 'complaint-summary',
      title: 'Complaint Summary Report',
      description: 'Summary of all complaints with categorization and resolution status',
      icon: FiFileText,
      format: 'PDF, Excel, CSV',
      tymeBank: true,
      framework: 'both',
      clientName: ''
    },
    {
      id: 'category-distribution',
      title: framework === 'NCD' ? 'NCD Category Distribution' : 'TCF Outcome Distribution',
      description: framework === 'NCD' 
        ? 'Distribution of complaints across NCD categories with detailed breakdown'
        : 'Distribution of complaints across TCF outcomes with detailed breakdown',
      icon: FiPieChart,
      format: 'PDF, Excel',
      tymeBank: true,
      framework: 'both',
      clientName: ''
    },
    {
      id: 'trend-analysis',
      title: 'Trend Analysis Report',
      description: 'Analysis of complaint trends over time with key insights and recommendations',
      icon: FiBarChart2,
      format: 'PDF',
      tymeBank: true,
      framework: 'both',
      clientName: ''
    },
    {
      id: 'product-closure',
      title: 'Product Closure Report',
      description: 'Comprehensive analysis of a specific product with AI-generated insights and recommendations',
      icon: FiClipboard,
      format: 'PDF, Interactive',
      tymeBank: true,
      framework: 'both',
      clientName: ''
    },
    {
      id: 'tyme-regulatory',
      title: 'Tyme Bank Regulatory Report',
      description: 'Consolidated regulatory report for South African financial regulatory authorities (FSCA/SARB)',
      icon: FiFileText,
      format: 'PDF, Excel',
      tymeBank: true,
      new: true,
      framework: 'clientCustom',
      clientName: 'Tyme Bank'
    },
    {
      id: 'tyme-root-cause',
      title: 'Root Cause Analysis Report',
      description: 'Detailed analysis of complaint root causes with recommended systemic improvements',
      icon: FiActivity,
      format: 'PDF, Interactive',
      tymeBank: true,
      new: true,
      framework: 'both',
      clientName: ''
    },
    {
      id: 'tcf-outcomes-analysis',
      title: 'TCF Outcomes Analysis',
      description: 'Detailed analysis of complaint outcomes against TCF principles with recommendations',
      icon: FiBarChart2,
      format: 'PDF, Excel',
      tymeBank: true,
      new: true,
      framework: 'TCF',
      clientName: ''
    },
    {
      id: 'tcf-conduct-report',
      title: 'TCF Conduct Risk Report',
      description: 'Assessment of conduct risk based on TCF principles with key risk indicators',
      icon: FiInfo,
      format: 'PDF, Interactive',
      tymeBank: true,
      new: true,
      framework: 'TCF',
      clientName: ''
    },
    {
      id: 'tyme-financial-impact',
      title: 'Financial Impact Assessment',
      description: 'Analysis of financial impacts, refunds, and compensations related to customer complaints',
      icon: FiDollarSign,
      format: 'PDF, Excel',
      tymeBank: true,
      new: true,
      framework: 'clientCustom',
      clientName: 'Tyme Bank'
    },
    {
      id: 'tyme-health-report',
      title: 'TymeHealth Service Evaluation',
      description: 'Comprehensive analysis of TymeHealth service complaints and usage statistics',
      icon: FiActivity,
      format: 'PDF, Excel',
      tymeBank: true,
      new: true,
      framework: 'clientCustom',
      clientName: 'Tyme Bank'
    },
    {
      id: 'tyme-moretyme-report',
      title: 'MoreTyme Service Performance',
      description: 'Analysis of the buy-now-pay-later service including customer satisfaction and payment completion metrics',
      icon: FiBarChart2,
      format: 'PDF, Interactive',
      tymeBank: true,
      new: true,
      framework: 'clientCustom',
      clientName: 'Tyme Bank'
    },
    {
      id: 'tyme-branch-performance',
      title: 'Branch Performance Report',
      description: 'Complaint analysis by branch location with staff training recommendations',
      icon: FiMapPin,
      format: 'PDF, Excel',
      tymeBank: true,
      new: true,
      framework: 'clientCustom',
      clientName: 'Tyme Bank'
    },
    {
      id: 'tyme-executive-dashboard',
      title: 'Executive Dashboard',
      description: 'High-level analytics and KPIs for executive review with action recommendations',
      icon: FiPieChart,
      format: 'PDF, Interactive',
      tymeBank: true,
      new: true,
      framework: 'both',
      clientName: ''
    },
    {
      id: 'fnb-credit-report',
      title: 'FNB Credit Card Complaint Analysis',
      description: 'Detailed analysis of FNB credit card complaints with customer feedback insights',
      icon: FiBarChart2,
      format: 'PDF, Excel',
      tymeBank: false,
      new: true,
      framework: 'clientCustom',
      clientName: 'FNB'
    },
    {
      id: 'discovery-health-report',
      title: 'Discovery Health Claims Report',
      description: 'Analysis of health insurance claims processing and complaint resolution times',
      icon: FiActivity,
      format: 'PDF, Interactive',
      tymeBank: false,
      new: true,
      framework: 'clientCustom',
      clientName: 'Discovery'
    }
  ];
  
  // Filter report types based on:
  // 1. Framework-agnostic reports (both)
  // 2. Reports matching the current framework (NCD/TCF) 
  // 3. Client-specific custom reports for the current client
  const reportTypes = allReportTypes.filter(report => 
    report.framework === 'both' || 
    report.framework === framework || 
    (report.framework === 'clientCustom' && report.clientName === customer.name)
  );
  
  const recentReports = [
    {
      id: '001',
      title: 'EveryDay Account Closure Report',
      date: '2025-04-02',
      format: 'Interactive',
      size: '3.2 MB',
      type: 'closure',
      icon: FiClipboard
    },
    {
      id: '002',
      title: 'Tyme Bank Regulatory Report - Q1 2025',
      date: '2025-04-01',
      format: 'PDF',
      size: '4.5 MB',
      type: 'normal',
      icon: FiAward,
      new: true
    },
    {
      id: '003',
      title: 'Root Cause Analysis - Fee Transparency',
      date: '2025-03-29',
      format: 'Interactive',
      size: '2.7 MB',
      type: 'normal',
      icon: FiActivity,
      new: true
    },
    {
      id: '004',
      title: 'Branch Performance Analysis - March 2025',
      date: '2025-03-28',
      format: 'Excel',
      size: '3.1 MB',
      type: 'normal',
      icon: FiMapPin,
      new: true
    },
    {
      id: '005',
      title: 'Financial Impact Assessment - Q1 2025',
      date: '2025-03-26',
      format: 'Excel',
      size: '2.3 MB',
      type: 'normal',
      icon: FiDollarSign,
      new: true
    },
    {
      id: '006',
      title: 'Executive Dashboard - March 2025',
      date: '2025-03-25',
      format: 'Interactive',
      size: '1.8 MB',
      type: 'normal',
      icon: FiTrendingUp,
      new: true
    }
  ];
  
  const handleViewReport = (report: any) => {
    if (report.type === 'closure') {
      setClosureReportId(report.id);
      setViewingClosureReport(true);
    } else {
      // Handle downloading normal reports
      console.log('Downloading report:', report.id);
    }
  };
  
  return (
    <div>
      {viewingClosureReport ? (
        <ClosureReport 
          reportId={closureReportId} 
          onClose={handleCloseClosureReport} 
        />
      ) : (
        <>
          <div className="mb-8 flex justify-between items-center">
            <div>
              <Heading level={1} weight="bold" className="text-3xl mb-2">Reports & Exports</Heading>
              <p className="text-secondary/70">Generate and download reports for compliance and analysis</p>
            </div>
          </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Report Types */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Generate New Report</h2>
            <p className="text-secondary/70 mb-6">Select a report type and configure options to generate a new report</p>
            
            <div className="space-y-4">
              {reportTypes.map(report => (
                <div 
                  key={report.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    selectedReport === report.id 
                      ? 'border-primary bg-primary/5' 
                      : report.new 
                        ? 'border-amber-300 hover:border-primary/30 bg-amber-50/50' 
                        : 'border-gray-200 hover:border-primary/30'
                  }`}
                  onClick={() => handleReportSelect(report.id)}
                >
                  <div className="flex items-start">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-4 flex-shrink-0">
                      <IconWrapper icon={report.icon} size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-secondary">{report.title}</h3>
                        {report.new && (
                          <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full font-medium">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-secondary/70 mt-1">{report.description}</p>
                      <div className="mt-2 text-xs text-secondary/50">Available formats: {report.format}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {selectedReport && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h3 className="font-medium mb-4">Report Options</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-secondary/70 mb-2">
                      <div className="flex items-center">
                        <IconWrapper icon={FiCalendar} className="mr-2" size={16} />
                        Start Date
                      </div>
                    </label>
                    <input 
                      type="date" 
                      className="w-full p-2 border border-gray-200 rounded-lg"
                      value={dateRange.start}
                      onChange={(e) => handleDateChange(e, 'start')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-secondary/70 mb-2">
                      <div className="flex items-center">
                        <IconWrapper icon={FiCalendar} className="mr-2" size={16} />
                        End Date
                      </div>
                    </label>
                    <input 
                      type="date" 
                      className="w-full p-2 border border-gray-200 rounded-lg"
                      value={dateRange.end}
                      onChange={(e) => handleDateChange(e, 'end')}
                    />
                  </div>
                </div>
                
                {framework === 'NCD' && (
                  <div className="mb-4">
                    <label className="block text-sm text-secondary/70 mb-2">
                      <div className="flex items-center">
                        <IconWrapper icon={FiFilter} className="mr-2" size={16} />
                        NCD Categories
                      </div>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center text-sm">
                        <input type="checkbox" className="mr-2" defaultChecked />
                        Consumer Understanding
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" className="mr-2" defaultChecked />
                        Products and Services
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" className="mr-2" defaultChecked />
                        Price and Value
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" className="mr-2" defaultChecked />
                        Consumer Support
                      </label>
                    </div>
                  </div>
                )}
                
                {framework === 'TCF' && (
                  <div className="mb-4">
                    <label className="block text-sm text-secondary/70 mb-2">
                      <div className="flex items-center">
                        <IconWrapper icon={FiFilter} className="mr-2" size={16} />
                        TCF Outcomes
                      </div>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center text-sm">
                        <input type="checkbox" className="mr-2" defaultChecked />
                        Outcome 1: Culture & Governance
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" className="mr-2" defaultChecked />
                        Outcome 2: Product Design
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" className="mr-2" defaultChecked />
                        Outcome 3: Clear Communication
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" className="mr-2" defaultChecked />
                        Outcome 4: Suitable Advice
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" className="mr-2" defaultChecked />
                        Outcome 5: Performance & Standards
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" className="mr-2" defaultChecked />
                        Outcome 6: Claims & Complaints
                      </label>
                    </div>
                  </div>
                )}
                
                <div className="mb-4">
                  <label className="block text-sm text-secondary/70 mb-2">Format</label>
                  <select className="w-full p-2 border border-gray-200 rounded-lg">
                    <option value="pdf">PDF</option>
                    <option value="excel">Excel</option>
                    <option value="csv">CSV</option>
                  </select>
                </div>
                
                <div className="mt-6 flex justify-end">
                  <button 
                    className="px-4 py-2 bg-primary text-secondary rounded-lg hover:bg-primary/90 transition-colors flex items-center"
                    onClick={handleGenerateReport}
                  >
                    <IconWrapper icon={FiDownload} className="mr-2" size={18} />
                    Generate Report
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Right Panel - Recent Reports */}
        <div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold mb-4">Recent Reports</h2>
            
            <div className="space-y-4">
              {recentReports.map(report => (
                <div key={report.id} className={`p-4 border rounded-lg transition-all ${
                  report.new ? 'border-amber-300 bg-amber-50/50 hover:border-primary/30' : 'border-gray-200 hover:border-primary/30'
                }`}>
                  <div className="flex items-start">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3 flex-shrink-0">
                      <IconWrapper icon={report.icon || FiFileText} size={16} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-secondary text-sm">{report.title}</h3>
                        {report.new && (
                          <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full font-medium">
                            New
                          </span>
                        )}
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-secondary/70">
                        <span>Generated: {report.date}</span>
                        <span>{report.format} • {report.size}</span>
                      </div>
                      <div className="mt-3">
                        <button 
                          className="text-primary text-xs font-medium flex items-center hover:underline"
                          onClick={() => handleViewReport(report)}
                        >
                          <IconWrapper icon={report.type === 'closure' ? FiExternalLink : FiDownload} className="mr-1" size={14} />
                          {report.type === 'closure' ? 'View Report' : 'Download'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 text-center">
              <button className="text-primary text-sm font-medium flex items-center mx-auto hover:underline">
                View All Reports
                <IconWrapper icon={FiExternalLink} className="ml-1" size={14} />
              </button>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
            <h2 className="text-lg font-bold mb-4">Scheduled Reports</h2>
            
            <div className="p-4 border border-gray-200 rounded-lg">
              <h3 className="font-medium text-secondary text-sm">Monthly Compliance Report</h3>
              <p className="text-xs text-secondary/70 mt-1">Automatically generated on the 1st of every month</p>
              <div className="flex justify-between mt-3 text-xs">
                <span className="text-green-600">Active</span>
                <button className="text-primary font-medium hover:underline">Edit</button>
              </div>
            </div>
            
            <div className="mt-4 text-center">
              <button className="px-4 py-2 border border-primary text-primary rounded-lg text-sm hover:bg-primary/5 transition-colors">
                Configure Scheduled Reports
              </button>
            </div>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default ReportExport;